import { useCallback, useEffect, useRef, useState } from 'react';
import { Client } from '@stomp/stompjs';
import SockJS from 'sockjs-client';
import { WS_URL } from '../api/axiosClient';
import chatService from '../api/chatService';

/**
 * Live support chat for one conversation.
 *  - loads history over REST
 *  - opens a STOMP connection and subscribes to /topic/chat.{conversationId}
 *  - send() publishes to /app/chat.send (REST fallback if the socket is down)
 */
export default function useChatSocket({ conversationId, senderId, senderName, senderType }) {
  const [messages, setMessages] = useState([]);
  const [connected, setConnected] = useState(false);
  const [loading, setLoading] = useState(true);
  const clientRef = useRef(null);

  const merge = useCallback((msg) => {
    setMessages((prev) => {
      if (msg.id && prev.some((m) => m.id === msg.id)) return prev;
      if (!msg.id && prev.some((m) => m.content === msg.content && m.createdAt === msg.createdAt)) return prev;
      return [...prev, msg];
    });
  }, []);

  useEffect(() => {
    if (!conversationId) return undefined;
    let active = true;
    setLoading(true);

    chatService
      .history(conversationId)
      .then((list) => active && setMessages(Array.isArray(list) ? list : []))
      .catch(() => active && setMessages([]))
      .finally(() => active && setLoading(false));

    const client = new Client({
      webSocketFactory: () => new SockJS(WS_URL),
      reconnectDelay: 4000,
      onConnect: () => {
        if (!active) return;
        setConnected(true);
        client.subscribe(`/topic/chat.${conversationId}`, (frame) => {
          try {
            merge(JSON.parse(frame.body));
          } catch { /* ignore */ }
        });
      },
      onDisconnect: () => active && setConnected(false),
      onWebSocketError: () => active && setConnected(false),
      onStompError: () => active && setConnected(false),
    });
    client.activate();
    clientRef.current = client;

    return () => {
      active = false;
      if (client.active) client.deactivate();
    };
  }, [conversationId, merge]);

  const send = useCallback(
    async (content) => {
      const text = content.trim();
      if (!text) return;
      const payload = { conversationId, senderId, senderName, senderType, content: text };
      if (clientRef.current?.connected) {
        clientRef.current.publish({ destination: '/app/chat.send', body: JSON.stringify(payload) });
      } else {
        try {
          const saved = await chatService.send(payload);
          merge(saved);
        } catch { /* surfaced by caller */ }
      }
    },
    [conversationId, senderId, senderName, senderType, merge]
  );

  return { messages, connected, loading, send };
}
